import { useSession } from "@src/appV2/Auth/api/useSession";
import { CHARTING_INCOMPATIBLE_WORKPLACE_TYPES } from "@src/appV2/Charting/constants";
import { useUpdateChartingRecall } from "@src/appV2/FacilityProfile/api/useUpdateChartingRecall";
import { useUpdateIndependentContractorBadge } from "@src/appV2/FacilityProfile/api/useUpdateIndependentContractorBadge";
import { useUpdateSignOutWithQrCode } from "@src/appV2/FacilityProfile/api/useUpdateSignOutWithQrCode";
import { useTu } from "@src/appV2/i18n/useTu";
import { IndependentContractorBadgeGeneratedBy } from "@src/appV2/IndependentContractorBadge/analytics";
import { APP_V2_APP_EVENTS, useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useState } from "react";

import { type SettingsPermissions } from "../../types";
import { ComplianceSectionView } from "./ComplianceSectionView";

interface ComplianceSectionContainerProps {
  guardSettingsAction: (action: () => void) => void;
  permissions: SettingsPermissions;
  workplaceAttributes:
    | {
        independentContractorBadge: { enabled: boolean };
        name: string;
        signOutWithQrCode?: boolean;
        chartingRecall?: {
          enabled: boolean;
          recallGracePeriodDurationInHours: number;
        };
        type?: string;
      }
    | undefined;
  workplaceId: string;
  onSettingsChanged: () => void;
}

// Matches HCF_USER_EVENTS.INDEPENDENT_CONTRACTOR_BADGE_TOGGLED from legacy constants
const BADGE_TOGGLED_EVENT =
  "Workplace Web: Update Preference - Independent Contractor Badge Toggled";

const DEFAULT_GRACE_PERIOD_HOURS = 24;

export function ComplianceSectionContainer({
  guardSettingsAction,
  permissions,
  workplaceAttributes,
  workplaceId,
  onSettingsChanged,
}: ComplianceSectionContainerProps) {
  const { tu } = useTu("settings");
  const session = useSession();
  const { showSuccessToast, showErrorToast } = useToast();

  const [badgeEnabled, setBadgeEnabled] = useState(
    workplaceAttributes?.independentContractorBadge.enabled ?? false
  );
  const [qrCodeEnabled, setQrCodeEnabled] = useState(
    workplaceAttributes?.signOutWithQrCode ?? false
  );
  const [chartingRecallEnabled, setChartingRecallEnabled] = useState(
    workplaceAttributes?.chartingRecall?.enabled ?? false
  );
  const [gracePeriodHours, setGracePeriodHours] = useState(
    workplaceAttributes?.chartingRecall?.recallGracePeriodDurationInHours ??
      DEFAULT_GRACE_PERIOD_HOURS
  );

  const { isLoading: isBadgeUpdating, mutateAsync: updateBadge } =
    useUpdateIndependentContractorBadge();
  const { isLoading: isQrCodeUpdating, mutateAsync: updateQrCode } = useUpdateSignOutWithQrCode();
  const { isLoading: isChartingUpdating, mutateAsync: updateChartingRecall } =
    useUpdateChartingRecall();

  const isUpdating = isBadgeUpdating || isQrCodeUpdating || isChartingUpdating;

  const isChartingRecallVisible =
    workplaceAttributes?.type !== undefined &&
    !CHARTING_INCOMPATIBLE_WORKPLACE_TYPES.includes(workplaceAttributes.type);

  const getBadgeAnalyticsMeta = () => {
    return permissions.isAdmin
      ? {
          userId: session?.adminProfile?.userId,
          userName: session?.adminProfile?.name,
          generatedBy: IndependentContractorBadgeGeneratedBy.ADMIN,
        }
      : {
          userId: session?.user?._id,
          userName: session?.user?.name,
          generatedBy: IndependentContractorBadgeGeneratedBy.WORKPLACE,
        };
  };

  const handleBadgeToggle = async (checked: boolean) => {
    const userMeta = getBadgeAnalyticsMeta();
    logEvent(BADGE_TOGGLED_EVENT, {
      workplaceId,
      workplaceName: workplaceAttributes?.name,
      ...userMeta,
      value: checked,
    });

    setBadgeEnabled(checked);
    try {
      await updateBadge({ workplaceId, enabled: checked });
      showSuccessToast(tu("Badge requirement updated successfully"));
      onSettingsChanged();
    } catch {
      logEvent(APP_V2_APP_EVENTS.INDEPENDENT_CONTRACTOR_BADGE_SETTING_UPDATE_FAILURE, {
        workplaceId,
        ...userMeta,
        value: checked,
      });
      showErrorToast(tu("Error updating badge requirement. Please try again later."));
      setBadgeEnabled(!checked);
    }
  };

  const handleQrCodeToggle = async (checked: boolean) => {
    setQrCodeEnabled(checked);
    try {
      await updateQrCode({ workplaceId, signOutWithQrCode: checked });
      showSuccessToast(tu("QR code sign out updated successfully"));
      onSettingsChanged();
    } catch {
      showErrorToast(tu("Error updating QR code sign out. Please try again later."));
      setQrCodeEnabled(!checked);
    }
  };

  const handleChartingRecallToggle = async (checked: boolean) => {
    setChartingRecallEnabled(checked);
    try {
      await updateChartingRecall({
        workplaceId,
        chartingRecall: {
          enabled: checked,
          recallGracePeriodDurationInHours: gracePeriodHours,
        },
      });
      showSuccessToast(tu("Charting compliance updated successfully"));
      onSettingsChanged();
    } catch {
      showErrorToast(tu("Error updating charting compliance. Please try again later."));
      setChartingRecallEnabled(!checked);
    }
  };

  const handleGracePeriodChange = async (value: number) => {
    const previousValue = gracePeriodHours;
    setGracePeriodHours(value);
    try {
      await updateChartingRecall({
        workplaceId,
        chartingRecall: {
          enabled: chartingRecallEnabled,
          recallGracePeriodDurationInHours: value,
        },
      });
      showSuccessToast(tu("Grace period updated successfully"));
      onSettingsChanged();
    } catch {
      showErrorToast(tu("Error updating grace period. Please try again later."));
      setGracePeriodHours(previousValue);
    }
  };

  return (
    <ComplianceSectionView
      badgeEnabled={badgeEnabled}
      chartingRecallEnabled={chartingRecallEnabled}
      gracePeriodHours={gracePeriodHours}
      isChartingRecallVisible={isChartingRecallVisible}
      isUpdating={isUpdating}
      permissions={permissions}
      qrCodeEnabled={qrCodeEnabled}
      onBadgeToggle={(checked) => {
        guardSettingsAction(() => {
          void handleBadgeToggle(checked);
        });
      }}
      onChartingRecallToggle={(checked) => {
        guardSettingsAction(() => {
          void handleChartingRecallToggle(checked);
        });
      }}
      onGracePeriodChange={(value) => {
        guardSettingsAction(() => {
          void handleGracePeriodChange(value);
        });
      }}
      onQrCodeToggle={(checked) => {
        guardSettingsAction(() => {
          void handleQrCodeToggle(checked);
        });
      }}
    />
  );
}
